// /handoff — consumes the one-time KV token minted by the admin console when
// a verso_admin clicks "Open Org View". The token is stored in KV under
// handoff:<uuid>; GET /api/v1/auth/handoff?token=<uuid> reads + deletes it
// (one-time-use) and returns the impersonation JWT. startImpersonation()
// stashes the JWT in memory + sessionStorage and navigates to /.

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';

export default function Handoff() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [error, setError] = useState(null);
  const [consumed, setConsumed] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token || consumed) return;

    async function consumeHandoff() {
      try {
        setConsumed(true);
        const result = await api.get('/auth/handoff', { token });
        const data = result.data || result;

        // Set the impersonation token in memory + sessionStorage and refresh
        // /me — deliberately does NOT re-init the session (would race and
        // wipe the impersonation token).
        await startImpersonation(data.impersonation_token);

        navigate('/', { replace: true });
      } catch (err) {
        setError(err.body?.error || err.message || 'Handoff failed');
      }
    }

    consumeHandoff();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-gray-900">Handoff Failed</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <p className="mt-4 text-xs text-gray-400">Return to the admin console and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="mt-4 text-sm text-gray-500">Opening organization view...</p>
      </div>
    </div>
  );
}
